import React, { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { createAPIClient, ICompanySettingsApi, IEstimatesApi } from '../../api';
import * as types from '../../api/types';
import { PrintLayout, usePrintData } from '../../components/PrintLayout';
import { invoiceDate, invoiceMoney } from '../invoiceUi';
import { bundleDayLabel, estimateAddress, estimatePath } from '../estimates/estimateUi';

interface EstimatePrintData {
  estimate: types.Estimate;
  company: types.CompanySettings;
}

const cell = 'px-2 py-2 align-top';
const numericCell = `${cell} text-right whitespace-nowrap`;

/** A mark across the printed estimate when it is not (or no longer) an open offer. */
export interface EstimateMarkInfo {
  label: 'DRAFT' | 'WITHDRAWN' | 'EXPIRED' | 'DECLINED';
  note: string;
}

export function estimateMarkFor(status: types.EstimateStatus): EstimateMarkInfo | null {
  switch (status) {
    case 'DRAFT': return { label: 'DRAFT', note: 'Draft: this estimate has not been sent.' };
    case 'CANCELLED': return { label: 'WITHDRAWN', note: 'This estimate was withdrawn.' };
    case 'EXPIRED': return { label: 'EXPIRED', note: 'This estimate has expired.' };
    case 'DECLINED': return { label: 'DECLINED', note: 'This estimate was declined.' };
    default: return null;
  }
}

/** /print/estimates/:id (tracker item 39): the estimate as the customer receives it, without internal notes. */
export function EstimatePrintPage() {
  const { id = '' } = useParams<{ id: string }>();
  const api = useMemo<IEstimatesApi & ICompanySettingsApi>(() => createAPIClient(), []);
  const state = usePrintData<EstimatePrintData>(id, async () => {
    const [estimate, company] = await Promise.all([api.getEstimate(id), api.getCompanySettings()]);
    return { estimate, company };
  }, 'Could not load estimate');
  const estimate = state.data?.estimate;
  const title = estimate ? `Estimate ${estimate.estimateNumber} - ${estimate.customer.name}` : 'Estimate';

  return (
    <PrintLayout title={title} backTo={estimatePath(id)} backLabel="Back to estimate" noun="estimate" state={state} company={state.data?.company ?? null}>
      {estimate && <EstimateDocument estimate={estimate} />}
    </PrintLayout>
  );
}

function EstimateDocument({ estimate }: { estimate: types.Estimate }) {
  const hasDiscounts = estimate.discounts.length > 0;
  const mark = estimateMarkFor(estimate.status);
  const headings = ['Description', 'Qty', 'Unit price', 'Amount', ...(hasDiscounts ? ['Discount'] : []), 'Tax'];
  return (
    <>
      <section className="print-keep mb-8 flex flex-wrap items-start justify-between gap-6">
        <div>
          <h1 className="text-2xl font-bold tracking-wide">ESTIMATE</h1>
          <p className="mt-1 text-base font-semibold">#{estimate.estimateNumber}</p>
          {estimate.title && <p className="mt-1">{estimate.title}</p>}
          {mark && (
            <div className="print-keep mt-3" data-mark={mark.label}>
              <p className="inline-block -rotate-3 border-4 border-red-700 px-4 py-1 text-3xl font-black tracking-[0.3em] text-red-700">{mark.label}</p>
              <p className="mt-2 font-semibold text-red-700">{mark.note}</p>
            </div>
          )}
        </div>
        <dl className="grid grid-cols-[auto_auto] gap-x-4 gap-y-1">
          <dt className="text-gray-600">Date</dt><dd>{invoiceDate((estimate.sentAt ?? estimate.createdAt).slice(0, 10))}</dd>
          <dt className="text-gray-600">Valid until</dt><dd>{invoiceDate(estimate.expiryDate)}</dd>
        </dl>
      </section>

      <section className="print-keep mb-8 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <h2 className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-600">Prepared for</h2>
          <p className="font-semibold">{estimate.customer.name}</p>
          {estimate.customer.email && <p>{estimate.customer.email}</p>}
        </div>
        <div>
          <h2 className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-600">Work at</h2>
          <p>{estimateAddress(estimate.property)} {estimate.property.zip}</p>
        </div>
      </section>

      {estimate.customerMessage && <p className="print-keep mb-6 whitespace-pre-wrap">{estimate.customerMessage}</p>}

      <div className="overflow-x-auto print:overflow-visible">
        <table className="print-table w-full border-collapse text-sm" aria-label="Estimate line items">
          <thead>
            <tr className="border-b-2 border-gray-800">
              {headings.map((heading, index) => <th key={heading} className={`${index === 0 ? 'text-left' : 'text-right'} px-2 py-2 font-semibold`}>{heading}</th>)}
            </tr>
          </thead>
          <tbody>
            {estimate.lineItems.length === 0
              ? <tr><td colSpan={headings.length} className={`${cell} text-gray-600`}>No line items.</td></tr>
              : estimate.lineItems.map((line) => (
                <tr key={line.id} className="border-b border-gray-200">
                  <td className={cell}>
                    {line.description}
                    {line.bundleName && line.bundleOffsetDays !== null && <span className="block text-xs text-gray-600">{line.bundleName} · {bundleDayLabel(line.bundleOffsetDays)}</span>}
                  </td>
                  <td className={numericCell}>{line.quantity}</td>
                  <td className={numericCell}>{invoiceMoney(line.unitPrice)}</td>
                  <td className={numericCell}>{invoiceMoney(line.lineAmount)}</td>
                  {hasDiscounts && <td className={numericCell}>{Number(line.lineDiscount) ? `−${invoiceMoney(line.lineDiscount)}` : '—'}</td>}
                  <td className={numericCell}>{invoiceMoney(line.lineTax)}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      <dl className="print-keep mt-6 ml-auto max-w-xs space-y-1" aria-label="Estimate totals">
        <div className="flex justify-between gap-6"><dt>Subtotal</dt><dd>{invoiceMoney(estimate.subtotal)}</dd></div>
        {estimate.discounts.map((d, i) => <div key={i} className="flex justify-between gap-6"><dt>{d.name}</dt><dd>−{invoiceMoney(d.amount)}</dd></div>)}
        <div className="flex justify-between gap-6"><dt>Tax</dt><dd>{invoiceMoney(estimate.totalTax)}</dd></div>
        <div className="flex justify-between gap-6 border-t-2 border-gray-800 pt-2 text-base font-bold"><dt>Total</dt><dd>{invoiceMoney(estimate.total)}</dd></div>
      </dl>

      <section className="print-keep mt-10 grid grid-cols-1 gap-8 text-sm sm:grid-cols-2" aria-label="Acceptance">
        <div><div className="h-10 border-b border-gray-800" /><p className="mt-1 text-gray-600">Accepted by (signature)</p></div>
        <div><div className="h-10 border-b border-gray-800" /><p className="mt-1 text-gray-600">Date</p></div>
      </section>
    </>
  );
}
